import React from 'react';
import { Helmet } from 'react-helmet';
import queryString from 'query-string';
import { differenceInMilliseconds, addMilliseconds } from 'date-fns';
import {
  applicationRoutes,
  dynamicRoutes, dynamicRoutesKeys
} from './app_routes';
import {
  isKA,
  matchPaths,
  paramOrNul,
  toCamelCase,
  convertStringToCamelCase,
  isPS,
  getDataFromStorage,
  adjustTimezone,
  getIsPayg
} from '../../common/config/utils';
import Const, { purchaseFlowTypes, liveModeState } from './const';
import  i18n from './i18n';
import env from './variables';
import { buildExternalRouteTarget, externalRoutes } from './external_routes';

const {
  XFM_ADDITIONAL_FREE_SETUP_TIME, XFM_PROGRAM_ACTIVE, DEFAULT_XFM_CREDITS
} = env;

export const parseAdvisorsFromAdvisorsState = (advisors) => {
  const advisorsState = window.store.getState().advisors;
  if (!advisors || advisors === undefined) return [];
  if (!advisorsState) return advisors;
  return advisors.map((a) => ({ ...a, ...advisorsState[a.id] }));
};

export const convertErrorArrayToString = (fullMessages) => {
  let message = '';
  fullMessages.forEach((m, index) => {
    message = `${ message }${ index === 0 ? '' : ', ' }${ m }`;
  });
  return message;
};

export const advisorModeIsActive = (modeUntil) => !!(new Date(modeUntil) > new Date());

export const filterIsApplied = ({ withSearch = false }) => {
  const {
    includeOffline, minNumberOfReviewsGe, ppmGe, ppmLe, readingVia, query
  } = toCamelCase(queryString.parse(window.location.search));
  let filters = {
    includeOffline, minNumberOfReviewsGe, ppmGe, ppmLe, readingVia
  };
  if (withSearch) filters = { ...filters, query };
  const cleanFilters = Object.keys(filters)
    .filter((key) => filters[key] !== undefined);
  return Object.keys(cleanFilters).length > 0;
};

export const passwordIsValid = (password, isSignUp) => {
  if (typeof password !== 'string') return false;
  return isSignUp ? password.trim().length >= 1 : password.trim().length >= 6;
};

export const canShowAdvisorRate = (likesCount, dislikesCount, minReviewsNumberForDisplayingRating) => (
  likesCount + dislikesCount >= minReviewsNumberForDisplayingRating
);

export const cardsIndexes = () => {
  const indexArray = [];
  for (let index = 0; index < 15; index += 1) {
    indexArray.push(index);
  }
  return indexArray;
};

export const roundCredits = credit => {
  const formattedCredit = Number(credit);
  return Number.isNaN(formattedCredit) ? null : formattedCredit.toFixed(2);
};

export const subscribedToAdvisor = (availabilityNotification) => availabilityNotification && availabilityNotification.switchedOn;

export const textTruncate = (str, strLength, strEnding) => {
  if (!str) return str;
  let length = strLength;
  let ending = strEnding;
  if (length == null) length = 100;
  if (ending == null) ending = '...';
  if (str.length > length) return str.substring(0, length - ending.length) + ending;
  return str;
};

export const payPalProcessingFailReason = (errorType) => {
  if (errorType) return errorType.split('_').join(' ');
  return 'error';
};

export const makeMoreReadable = (text) => {
  if ((text || '').length < 400) return text;
  let moreReadableText = '';
  let moreReadableTextPart = '';
  let leftText = text;
  let i = 0;
  do {
    moreReadableTextPart = '';
    if (i === 0) {
      moreReadableTextPart = leftText.substring(0, leftText.indexOf('.') + 1);
    } else {
      const firstPart = leftText.substring(0, 200);
      const secondPart = leftText.substring(200);
      const secondPartforDotOrNewLine = secondPart.substring(0, secondPart.indexOf('.') + 1) || secondPart.substring(0, secondPart.indexOf('\n'));
      if (secondPartforDotOrNewLine) {
        moreReadableTextPart = firstPart + secondPartforDotOrNewLine;
      } else {
        moreReadableTextPart = firstPart + secondPart;
      }
    }
    moreReadableText += `${ moreReadableTextPart.replace(/^\s+/, '') }\n`;
    leftText = leftText.substring(moreReadableTextPart.length);
    i += 1;
  }
  while (leftText.length >= 300);
  moreReadableText += leftText.replace(/^\s+/, '');
  return moreReadableText;
};

export const decodeHtmlBracketCharCodes = str => str.replace(/&lt;/g, '<').replace(/&gt;/g, '>');

export const advisorsEmptyList = (count, loading = false) => {
  const emptyList = [];
  for (let index = 0; index < count; index += 1) {
    const advisor = loading ? { loading: true } : {};
    emptyList.push({ ...advisor });
  }
  return emptyList;
};

export const pathsNameWithLocalization = (object) => {
  const pathsArray = [];
  Object.keys(object).map((key) => pathsArray.push(object[key]));
  return pathsArray;
};

const onHeaderSearchClickAction = (isSearchLineCloseButtonClick, searchInputRef, t) => {
  if (isSearchLineCloseButtonClick) return null;
  const value = searchInputRef.current?.value.trim();
  window.location.assign(buildExternalRouteTarget(t, externalRoutes.aiSearchResult, {
    queryParams: { q: value }
  }));
  return null;
};

const headerSearchLineButtonDisabled = ({ searchInputRef, isSearchLineCloseButtonClick }) => {
  const { query } = queryString.parse(window.location.search);
  const searchInputRefValue = ((searchInputRef || {}).current || {}).value;
  return !((searchInputRefValue.length !== 0
    && searchInputRefValue !== query) || isSearchLineCloseButtonClick);
};

export const getValueOrDefault = (obj, ...keys) => {
  while (keys.length) {
    const key = keys.shift();
    if (!obj || !(key in obj)) {
      return {};
    }
    obj = obj[key];
  }

  return obj;
};

export const onHeaderSearchClick = ({
  searchInputRef, setSearchInputValue, isSearchLineCloseButtonClick, t
}) => {
  if (headerSearchLineButtonDisabled({ searchInputRef, isSearchLineCloseButtonClick })) return null;
  if (isSearchLineCloseButtonClick) {
    searchInputRef.current.value = ''; //eslint-disable-line
    setSearchInputValue('');
  }
  const searchValue =  searchInputRef.current.value.trim();
  if (searchValue.length <= 0) {
    searchInputRef.current.value = ''; //eslint-disable-line
    if (setSearchInputValue) setSearchInputValue('');
    return null;
  }

  onHeaderSearchClickAction(
    isSearchLineCloseButtonClick,
    searchInputRef,
    t
  );
  return null;
};

export const separateThousands = (value) => (value || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');

export const getCountryFlagUrl = (countryCode) => {
  if (!countryCode) return null;
  const countryFlags = window.config?.uiSettings?.country_flags ?? window.config?.uiSettings?.countryFlags;
  return countryFlags?.[countryCode] ?? null;
};

export const specialityNameOnTwoLine = (name, specialityNameClass) => {
  const words = name.split(' ');
  const firstLine = name.substring(0, name.indexOf(words[words.length - 1]));
  const secondLine = words[words.length - 1];
  return (
    <div className={ specialityNameClass }>
      <span suppressHydrationWarning>
        { firstLine }
      </span>
      <span suppressHydrationWarning>
        { secondLine }
      </span>
    </div>
  );
};

export const isAdvisorBusy = ({ liveModes }) => {
  let busy;
  liveModes.forEach((mode) => { if (mode.modeState === 'busy') busy = true; });
  return busy;
};

export const renderCanonicalLink = (url) => {
  const href = url || `${ window.location.origin }${ window.location.pathname }`;
  return (
    <Helmet>
      <link rel="canonical" href= { href } suppressHydrationWarning />
    </Helmet>
  );
};

export const isMobile = () => parseInt(getComputedStyle(document.documentElement).getPropertyValue('--window-width'), 10) <= window.shared.phoneOnlyUpSize;
export const isMiddleMobile = () => parseInt(getComputedStyle(document.documentElement).getPropertyValue('--window-width'), 10) <= window.shared.middlePhoneOnlyUpSize;
export const isPSMobile = () => isPS() && isMobile();

export const renderItemPropMeta = (itemPropSearch) => {
  if (!itemPropSearch.meta) return null;
  return Object.keys(itemPropSearch.meta).map((meta) => itemPropSearch.meta[meta]);
};

export const getSectionForEvent = ({ location }) => {
  if (location.state && location.state.section) return { section:  location.state.section };
  return {};
};

// Strip `click_source` from the current URL without triggering a React Router
// navigation. Used right after we read the value, so the param doesn't linger
// across refreshes or in-page navigations once consumed.
const consumeClickSourceFromUrl = () => {
  if (typeof window === 'undefined' || !window.history || !window.history.replaceState) return;
  const params = queryString.parse(window.location.search);
  if (!params.click_source) return;
  delete params.click_source;
  const newSearch = queryString.stringify(params);
  const newUrl = `${ window.location.pathname }${ newSearch ? `?${ newSearch }` : '' }${ window.location.hash }`;
  window.history.replaceState(window.history.state, '', newUrl);
};

export const getClickSourceForEvent = ({ location }) => {
  const fromQuery = location.search ? (queryString.parse(location.search) || {}).click_source : null;
  if (fromQuery) {
    consumeClickSourceFromUrl();
    return { clickSource: fromQuery };
  }
  if (location.state && location.state.clickSource) return { clickSource: location.state.clickSource };
  return { clickSource: 'direct' };
};

export const cleanupAdvisorFieldsForAnalytics = (advisor) => {
  const {
    ...rest
  } = advisor;
  const res = {};
  Object.keys(rest).forEach(key => {
    if (!(rest[key] instanceof Function)) {
      res[key] = rest[key];
    }
  });
  return res;
};

export const getAddFundsInitialChargedAndInitialDurationPropsNames = ({ chatType }) => {
  let initialChargedPropName = '';
  let initialDurationPropName = '';
  switch (chatType) {
    case Const.chatType.text:
      initialChargedPropName = 'chat charged initial';
      initialDurationPropName = 'chat minutes initial';
      break;
    case Const.chatType.voice:
      initialChargedPropName = 'voice charged initial';
      initialDurationPropName = 'voice minutes initial';
      break;
    case Const.chatType.video:
      initialChargedPropName = 'vc charged initial';
      initialDurationPropName = 'vc minutes initial';
      break;
    default:
      break;
  }
  return { initialChargedPropName, initialDurationPropName };
};

const getMinutes = ({ selectedDuration }) => {
  const minutes = selectedDuration / 60;
  if (Number.isInteger(minutes)) return minutes.toFixed();
  return minutes.toFixed(2);
};

const additionalOptionsName = {
  payAsYouGo: 'pay as you go',
  currentBalance: 'current balance'
};

export const getDefaultDuration = ({ pricingOptions, payAsYouGo }) => {
  if (Object.keys(pricingOptions).length === 0 && payAsYouGo) return additionalOptionsName.payAsYouGo;
  const defaultOption = pricingOptions.find((o) => o.default && !o.additional)?.duration;
  let defaultDuration = defaultOption < 0 ? defaultOption : (defaultOption / 60).toFixed(2);
  if (!defaultOption && pricingOptions.find((o) => o.default && o.additional)) {
    defaultDuration = additionalOptionsName.currentBalance;
  }
  if (defaultDuration === -1) defaultDuration = additionalOptionsName.payAsYouGo;
  return defaultDuration;
};

export const getDurationOptions = ({ pricingOptions, payAsYouGo }) => {
  if (Object.keys(pricingOptions).length === 0 && payAsYouGo) return [additionalOptionsName.payAsYouGo];
  const durationOptions = pricingOptions.map((o) => {
    if (o.duration === -1) return additionalOptionsName.payAsYouGo;
    if (o.additional) return additionalOptionsName.currentBalance;
    return o.duration;
  });
  return durationOptions;
};

const trackPaygRelatedProps = (payAsYouGo, propValue) => (payAsYouGo ? null : propValue);

export const getPayAsYouGoUsedProperty = ({ flow, isPayg }) => {
  if (flow !== purchaseFlowTypes.paymentMethods && flow !== purchaseFlowTypes.addFunds) {
    return { 'pay as you go used':  !!isPayg };
  }
  return null;
};

export const getDurationPickRelatedProps = ({
  requiredAmount, continuedSession, selectedDuration, price, advisorId, homepageSection,
  startChatClickSource, displayVersion, defaultDuration, defaultDurationChanged, durationOptions, time, payAsYouGo, flow, additional, topupFlow
}) => {
  const props = {
    ...requiredAmount,
    ...paramOrNul('continued session', continuedSession),
    ...paramOrNul('minutes', additional ? null : trackPaygRelatedProps(payAsYouGo, getMinutes({ selectedDuration }))),
    ...paramOrNul('expected charged seconds', trackPaygRelatedProps(payAsYouGo, selectedDuration)),
    ...paramOrNul('credits', trackPaygRelatedProps(payAsYouGo, price)),
    tryout: false,
    'advisor id': advisorId,
    ...paramOrNul('section', homepageSection),
    'click source': startChatClickSource,
    'display version': displayVersion,
    ...paramOrNul('default duration preselected', defaultDuration),
    'default duration changed': defaultDurationChanged,
    'duration options': durationOptions,
    'time on screen': parseFloat((time / 1000).toFixed(2)),
    ...getPayAsYouGoUsedProperty({ flow, isPayg: payAsYouGo }),
    flow,
    ...paramOrNul('top up flow', topupFlow)
  };
  return props;
};

export const getReviewCount = ({ likesCount, dislikesCount }) => {
  const reviewCount = likesCount + dislikesCount;
  return reviewCount;
};

export const formatChargeDuration = (chargeDuration) => {
  if (!chargeDuration && chargeDuration !== 0) return '';
  const hours = Math.floor(chargeDuration / 3600);
  const minutes = Math.floor((chargeDuration % 3600) / 60);
  const seconds = chargeDuration % 60;
  const showConvertedTime = (time, text) => (time > 0 ? text : '');
  const hoursText = showConvertedTime(hours, `${ hours } h `);
  const minutesText = showConvertedTime(minutes, `${ minutes } min `);
  const secondsText = showConvertedTime(seconds, `${ seconds } sec `);

  return `${ hoursText }${ minutesText }${ secondsText }`.trim();
};

export const getCurrentTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone;

export const convertDateTimeZone = ({ dateString, timeZoneString }) => new Date((new Date(dateString)).toLocaleString('en-US', { timeZone: timeZoneString }));

const flattenRouteSettings = (obj) => {
  const flattened = {};

  Object.keys(obj).forEach((key) => {
    if (key === 'translations') return;
    flattened[key] = obj[key];
  });

  const translationObj = obj.translations?.[i18n.language];

  Object.keys(translationObj).forEach((key) => {
    flattened[key] = translationObj[key];
  });

  return flattened;
};

export const getRouteSettings = ({ routeKey }) => {
  const route = i18n.t(routeKey);

  const routesSettings = dynamicRoutesKeys
    .filter(key => (dynamicRoutes[key].translations?.[i18n.language].slug || dynamicRoutes[key].slug) === route)
    .map(key => dynamicRoutes[key]);

  if (!routesSettings.length) return {};
  return flattenRouteSettings(routesSettings[0]);
};

export const isPrerenderMode = () => {
  const { mode } = toCamelCase(queryString.parse(window.location.search));
  return mode === 'prerender';
};

export const routeMatch = (pathnames) => {
  const match = matchPaths({ pathname: pathnames, path: `${ window.location.pathname }` });
  return match || {};
};

export const matchBannerRoutes = (routeKey) => {
  const { showBanner = false } = getValueOrDefault(applicationRoutes, routeKey);
  return showBanner;
};

export const shouldShowAffiliateBanner = (creditMatchingInfo, userShowMatchUpBonusInfo, userLoading, routeKey) => !!(creditMatchingInfo
    && Object.keys(creditMatchingInfo).length !== 0
    && (userShowMatchUpBonusInfo === null || userShowMatchUpBonusInfo)
    && !userLoading
    && matchBannerRoutes(routeKey));

export const getXfmDefaultMinutes = () =>  {
  const xfmDefaultMinutes = DEFAULT_XFM_CREDITS ?? 3;
  return parseInt(xfmDefaultMinutes, 10);
};

export const isXFMActive = () => XFM_PROGRAM_ACTIVE && XFM_PROGRAM_ACTIVE.toLowerCase() === 'true';

export const isXfmProgram = ({ advisorXfmProgram, userXfmProgram }) => {
  if (window.currentUserId) {
    return (!advisorXfmProgram || advisorXfmProgram.active) && userXfmProgram?.credits;
  }
  const wasLoggedIn = !!getDataFromStorage('WAS_LOGIN');
  if (wasLoggedIn) return false;
  return isXFMActive() && getXfmDefaultMinutes() > 0;
};

export const getXfmFreeSetupMin = () =>  {
  const freeSetupSeconds = XFM_ADDITIONAL_FREE_SETUP_TIME ?? 180;
  return  parseInt(freeSetupSeconds / 60, 10);
};

export const getModeColor = (mode) => {
  let defaultModesColors = { chat:'#62DD13', voice: '#8755F4', video: '#22B6FF' };
  if (isKA()) defaultModesColors = { chat: '#7BD137', voice: '#6C84FF', video: '#22B6FF' };
  else if (isPS()) defaultModesColors = { chat: '#2BA4FA', voice: '#B45092' };
  return defaultModesColors[mode];
};

export const isPayg = ({ duration }) => duration === -1;

export const setElementZIndex = (decreaseElementZIndex, low, high) => (decreaseElementZIndex ? low : high);

export const getAdvisorDataIfSsrMode = () => {
  if (!window.advisorsBySlug) return null;
  const advisorMatch = matchPaths({ pathname: Object.keys(window.advisorsBySlug), path: window.location.pathname });
  const advisorData = window.advisorsBySlug[advisorMatch?.pattern.path];

  return toCamelCase(advisorData);
};

export const getHomePageDataIfSsrMode = () => {
  if (isPrerenderMode()) return null;

  const homePageTemplate = document.getElementById(Const.templateNames.homePage);

  if (!homePageTemplate) return null;

  return JSON.parse(homePageTemplate.innerText);
};

export const getSearchResultsData = () => {
  if (isPrerenderMode()) return null;

  const searchResultsTemplate = document.getElementById(Const.templateNames.searchResults);

  if (!searchResultsTemplate) return null;
  return JSON.parse(searchResultsTemplate.innerText);
};

export const getActivePromotionData = () => {
  const anonymousUserTemplate = document.getElementById(Const.templateNames.activePromotion);

  if (!anonymousUserTemplate) return null;
  return JSON.parse(anonymousUserTemplate.innerText);
};

export const getHeaderHeight = () => {
  const header = document.getElementById('mainHeaderID')?.getBoundingClientRect();
  return `${ header?.height || 0 }px`;
};

export const getUserFirstActivePromotion = ({ user, application }) => {
  const userIsPresented = user && user.user?.id || user?.id;
  if (userIsPresented) {
    return user && user.user && (user.user.activePromotions || [])[0] || user && (user.activePromotions || [])[0] || {};
  }
  return application?.anonymousUser && (application.anonymousUser.activePromotions || [])[0] || {};
};

const realSalesPriceOrNull = ({ salesPrice, localPrice }) => (
  parseFloat(salesPrice) < parseFloat(localPrice) ? salesPrice : null
);

export const getAvailableSalesPrice = ({ localPromoPrices, userAdvisorModeSalePrice, localPrice }) => {
  if (!userAdvisorModeSalePrice || !localPromoPrices) return null;
  const salesPrice = localPromoPrices[convertStringToCamelCase(userAdvisorModeSalePrice)];
  return realSalesPriceOrNull({ salesPrice, localPrice });
};

export const getAvailableMinSalesPrice = ({ localLiveModeMinPromoPrices, userAdvisorModeMinSalesPrice, localPrice }) => {
  if (!userAdvisorModeMinSalesPrice || !localLiveModeMinPromoPrices) return null;
  const salesPrice = localLiveModeMinPromoPrices[convertStringToCamelCase(userAdvisorModeMinSalesPrice)];
  return realSalesPriceOrNull({ salesPrice, localPrice });
};

export const getUserMatchUpBonusInfo = ({ bonusInfos }) => {
  if (!bonusInfos) return null;
  const userMatchUpBonusInfo = bonusInfos.find((bonus) => bonus.bonusType === 'match_up');
  if (!userMatchUpBonusInfo) return {};
  return userMatchUpBonusInfo;
};

export const getTimeOnBubble = ({ time }) => {
  const now = new Date(time);
  const hours = now.getHours();
  const minutes = now.getMinutes();
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const hours12 = hours % 12 || 12;

  const formattedHours = hours12.toString().padStart(2, '0');
  const formattedMinutes = minutes.toString().padStart(2, '0');

  const timeOnBubble = `${ formattedHours }:${ formattedMinutes } ${  ampm }`;
  return timeOnBubble;
};

export const getAdvisorXfmProgram = ({ advisors, advisorId }) => {
  const advisor = (advisors || {})[parseInt(advisorId, 10)];
  return advisor && advisor.xfmProgram;
};

const localDate = new Date();
export const getDateFromServer = (utc) => {
  const currentDate = new Date();
  const milliSecondsDifference = differenceInMilliseconds(currentDate, localDate);

  let utcDate;
  if (utc) {
    utcDate = utc.includes('T') ? new Date(utc) : new Date(utc.replace(/-/g, '/'));
  } else {
    utcDate = new Date();
  }

  const dateFromServer = addMilliseconds(utcDate, milliSecondsDifference);
  return dateFromServer;
};

export const getDateFromServerUTC = (utc) => {
  const dateFromServer = getDateFromServer(utc);
  const dateFromServerUtc = adjustTimezone(dateFromServer);
  return dateFromServerUtc;
};

export const verifyBusyCarouselRender = (userId, advisorStatus, carouselAvailability, similarOnlineAdvisors) => (userId && (advisorStatus === liveModeState.busy || advisorStatus === liveModeState.offline)
&& carouselAvailability && similarOnlineAdvisors?.length > 0);

export const getPaygContext = () =>  {
  const { chat, voipChat } = window.store.getState();
  const fromMobilePayg = getIsPayg(toCamelCase(queryString.parse(window.location.search)).payg);
  return { payg_context: isPayg({ duration: chat?.duration || voipChat?.duration }) || fromMobilePayg };
};

// Current currency
export const isMulticurrencyAllowed = () => (window.store?.getState()?.user?.user?.multicurrencyAllowed || window.store?.getState()?.application?.dynamicConfig?.multicurrencyAllowed === true) || false;

export const getCurrentCurrencyCode = () => window.store?.getState()?.user?.user?.currency || window.store?.getState()?.application?.dynamicConfig?.currency || 'USD';

export const isDefaultCurrency = () => getCurrentCurrencyCode() === 'USD';

export const getCurrencySignForCode = (currencyCode) => {
  const currencyList = window.store?.getState()?.application?.dynamicConfig?.supportedCurrencies;
  return currencyList?.find(c => c.key === currencyCode)?.sign || '$';
};

export const formatLocalCurrencyNumberOnly = (amount) => {
  const { locale } = Intl.NumberFormat().resolvedOptions();
  const selectedLocale = locale || i18n.language || 'en';
  return Intl.NumberFormat(selectedLocale, { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(Math.abs(amount));
};

export const formatAmountForDisplay = (amount) => {
  const { locale } = Intl.NumberFormat().resolvedOptions();
  const selectedLocale = locale || i18n.language || 'en';
  return Intl.NumberFormat(selectedLocale, {
    maximumFractionDigits: 0,
    useGrouping: true
  }).format(Math.abs(amount));
};

/**
 * Formats a filter price the way the app does: a whole amount loses its decimals
 * (`5.00` reads as `5`), a fractional one keeps two (`5.50`)
 */
export const formatFilterAmount = (amount) => {
  const numericAmount = parseFloat(amount);
  // Bounds are missing until the dynamic config arrives — render zero, not NaN.
  if (!Number.isFinite(numericAmount)) return formatAmountForDisplay(0);
  return Number.isInteger(numericAmount)
    ? formatAmountForDisplay(numericAmount)
    : formatLocalCurrencyNumberOnly(numericAmount);
};

export const formatLocalCurrency = (amount, currencyCode) => {
  const code = (currencyCode != null && currencyCode !== '') ? currencyCode : getCurrentCurrencyCode();
  const currencySign = amount < 0 ? `-${ getCurrencySignForCode(code) }` : getCurrencySignForCode(code);
  if (!amount || amount === undefined || amount === null || amount === 0) return `${ currencySign }0.00`;
  const { locale } = Intl.NumberFormat().resolvedOptions();
  const selectedLocale = locale || i18n.language || 'en';
  return `${ currencySign }${ Intl.NumberFormat(selectedLocale, { maximumFractionDigits: 2, minimumFractionDigits: 2 }).format(Math.abs(amount)) }`;
};

export const generalCreditFormatForUSD = (amount) => formatLocalCurrency(amount, 'USD');

// Format phone number for display
export const formatPhoneNumber = (number) => {
  if (!number) return '';
  return number.startsWith('+') ? number : `+${ number }`;
};

export const getUuidFromChatConfig = (chat) => {
  const providers = chat?.redundancyChat?.providers;
  const uuid = chat?.pubnub?.uuid || providers?.pubnub?.uuid || providers?.ably?.clientId;
  return uuid;
};
